import React, { useState, useEffect } from 'react';

const SessionsListView = ({ conversations, onSelectSession, onDeleteSession, onBack }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleSelect = (id) => {
    setIsVisible(false);
    setTimeout(() => onSelectSession(id), 150);
  };

  // Group conversations by month
  const groupByMonth = (convos) => {
    const groups = {};
    convos.forEach((conv) => {
      const date = new Date(conv.updated_at || conv.created_at);
      const key = `${date.getFullYear()}-${date.getMonth()}`;
      const label = date.toLocaleDateString('en-US', { month: 'long' });
      if (!groups[key]) {
        groups[key] = { label, conversations: [] };
      }
      groups[key].conversations.push(conv);
    });
    // Return sorted by most recent month first
    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([, group]) => group);
  };

  const grouped = groupByMonth(conversations);

  return (
    <div
      data-testid="sessions-list-view"
      style={{
        position: 'fixed',
        inset: 0,
        background: '#080608',
        backgroundImage: 'radial-gradient(ellipse at 50% 40%, rgba(62, 28, 18, 0.4) 0%, rgba(8, 6, 8, 0) 70%)',
        zIndex: 50,
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 0.8s ease-out',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Velvet texture */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.035,
          pointerEvents: 'none',
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`
        }}
      />

      {/* Vignette */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: 'radial-gradient(ellipse at center, rgba(0,0,0,0) 40%, rgba(0,0,0,0.7) 100%)'
        }}
      />

      {/* Scrollable sessions */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          padding: '64px 32px 120px 32px',
          position: 'relative',
          zIndex: 2
        }}
      >
        <div style={{ maxWidth: '520px', margin: '0 auto' }}>
          {conversations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <div style={{
                width: '32px',
                height: '1px',
                background: 'rgba(196, 137, 74, 0.3)',
                margin: '0 auto 32px',
              }} />
              <p style={{
                fontFamily: "'Cormorant', Georgia, serif",
                fontSize: '20px',
                fontStyle: 'italic',
                fontWeight: 300,
                color: 'rgba(255, 243, 225, 0.55)',
                margin: '0 0 16px',
                lineHeight: 1.4,
              }}>
                Nothing here yet.
              </p>
              <p style={{
                fontFamily: "'Cormorant', Georgia, serif",
                fontSize: '17px',
                fontStyle: 'italic',
                fontWeight: 300,
                color: 'rgba(255, 243, 225, 0.30)',
                margin: 0,
                lineHeight: 1.6,
              }}>
                Your conversations will appear<br />here once you begin.
              </p>
            </div>
          ) : (
            grouped.map((group, gi) => (
              <div key={gi} style={{ marginBottom: '16px' }}>
                {/* Month header */}
                <div
                  style={{
                    textAlign: 'center',
                    padding: '32px 0 24px 0'
                  }}
                >
                  <span
                    data-testid={`month-header-${gi}`}
                    style={{
                      fontFamily: "'Cormorant', Georgia, serif",
                      fontSize: '13px',
                      fontWeight: 300,
                      fontStyle: 'italic',
                      color: 'rgba(255, 243, 225, 0.25)'
                    }}
                  >
                    {group.label}
                  </span>
                </div>

                {/* Sessions in this month */}
                {group.conversations.map((session) => (
                  <div
                    key={session.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      borderBottom: '1px solid rgba(255, 243, 225, 0.06)',
                    }}
                  >
                    <button
                      data-testid={`session-row-${session.id}`}
                      onClick={() => handleSelect(session.id)}
                      className="session-row-btn"
                      style={{
                        flex: 1,
                        padding: '20px 0',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'background 0.2s ease',
                        WebkitTapHighlightColor: 'transparent'
                      }}
                    >
                      <p
                        style={{
                          fontFamily: "'EB Garamond', Georgia, serif",
                          fontSize: '18px',
                          fontWeight: 400,
                          color: 'rgba(255, 243, 225, 0.82)',
                          margin: 0,
                          lineHeight: 1.5
                        }}
                      >
                        {session.title || 'Untitled conversation'}
                      </p>
                    </button>
                    <button
                      data-testid={`delete-session-${session.id}`}
                      onClick={() => setDeleteTarget({ id: session.id, title: session.title || 'Untitled conversation' })}
                      style={{
                        flexShrink: 0,
                        width: '36px',
                        height: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'rgba(255, 243, 225, 0.2)',
                        fontSize: '18px',
                        lineHeight: 1,
                        padding: 0,
                        WebkitTapHighlightColor: 'transparent',
                        transition: 'color 0.2s ease',
                      }}
                      className="delete-session-btn"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Fixed bottom bar */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 10
        }}
      >
        <div
          style={{
            height: '40px',
            background: 'linear-gradient(to bottom, transparent 0%, #080608 100%)',
            pointerEvents: 'none'
          }}
        />
        <div
          style={{
            height: '56px',
            background: '#080608',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 32px'
          }}
        >
          <button
            onClick={onBack}
            data-testid="back-to-home"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontFamily: "'Cormorant', Georgia, serif",
              fontSize: '13px',
              fontWeight: 300,
              fontStyle: 'italic',
              letterSpacing: '0.08em',
              color: 'rgba(255, 243, 225, 0.45)',
              padding: '12px 24px',
              transition: 'color 0.2s ease'
            }}
          >
            back to home
          </button>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div
          data-testid="delete-confirm-modal"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(8, 6, 8, 0.85)',
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '32px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#111010',
              border: '0.5px solid rgba(255, 243, 225, 0.1)',
              borderRadius: '12px',
              padding: '32px 28px',
              maxWidth: '320px',
              width: '100%',
              textAlign: 'center',
            }}
          >
            <p
              style={{
                fontFamily: "'EB Garamond', Georgia, serif",
                fontSize: '18px',
                color: 'rgba(255, 243, 225, 0.82)',
                lineHeight: 1.6,
                marginBottom: '8px',
              }}
            >
              Delete this conversation?
            </p>
            <p
              style={{
                fontFamily: "'EB Garamond', Georgia, serif",
                fontSize: '14px',
                color: 'rgba(255, 243, 225, 0.35)',
                marginBottom: '28px',
              }}
            >
              This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                data-testid="delete-confirm-cancel"
                onClick={() => setDeleteTarget(null)}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'transparent',
                  border: '0.5px solid rgba(255, 243, 225, 0.15)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontFamily: "'EB Garamond', Georgia, serif",
                  fontSize: '16px',
                  color: 'rgba(255, 243, 225, 0.5)',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                Cancel
              </button>
              <button
                data-testid="delete-confirm-delete"
                onClick={() => {
                  onDeleteSession(deleteTarget.id);
                  setDeleteTarget(null);
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'rgba(180, 60, 40, 0.25)',
                  border: '0.5px solid rgba(180, 60, 40, 0.4)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontFamily: "'EB Garamond', Georgia, serif",
                  fontSize: '16px',
                  color: 'rgba(255, 150, 130, 0.85)',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .session-row-btn:active {
          background: rgba(196, 137, 74, 0.04) !important;
        }
        .delete-session-btn:active {
          color: rgba(255, 243, 225, 0.55) !important;
        }
      `}</style>
    </div>
  );
};

export default SessionsListView;
